import React, { useEffect, useMemo, useState } from 'react';
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  FontSizeOutlined,
  FormOutlined,
  HighlightOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import times from 'img/times.svg';
import {
  NewTag,
  NotesButtonStyled,
  DrawerPanel,
  DrawerHeader,
  DrawerTitle,
  CloseButton,
  DrawerContent,
  DrawerHint,
  DrawerWarning,
  NotesTextArea,
  ModeToggle,
  ModeButton,
  DrawToolbar,
  ToolButton,
  ToolbarSpacer,
  CanvasArea,
  ResizeHandle,
} from './styles';
import { DrawingCanvas } from './DrawingCanvas';
import { useResizablePanel } from './useResizablePanel';
import {
  DrawingStroke,
  DrawTool,
  clearSimuladoDrawing,
  isDrawingSupported,
  safeGetDrawing,
  safeSetDrawing,
} from './drawing';

const NOTES_STORAGE_PREFIX = 'simuladoNotes-';

const getNotesStorageKey = (lessonId: number | string) => `${NOTES_STORAGE_PREFIX}${lessonId}`;

// sessionStorage pode lançar (Safari em navegação privada, storage bloqueado por política);
// como a anotação é conveniência, degradamos para "só em memória" em vez de quebrar a prova.
const safeGetNotes = (lessonId: number | string) => {
  try {
    return sessionStorage.getItem(getNotesStorageKey(lessonId)) || '';
  } catch {
    return '';
  }
};

const safeSetNotes = (lessonId: number | string, notes: string) => {
  try {
    sessionStorage.setItem(getNotesStorageKey(lessonId), notes);
  } catch {
    // sem storage disponível: a anotação vive só no estado do componente
  }
};

// Remove as anotações do simulado ao finalizar a prova (não persistem após o encerramento).
// Texto e desenho são descartados juntos, mantendo a regra atual das anotações.
export const clearSimuladoNotes = (lessonId: number | string) => {
  try {
    sessionStorage.removeItem(getNotesStorageKey(lessonId));
  } catch {
    // sem storage disponível: nada a limpar
  }
  clearSimuladoDrawing(lessonId);
};

export function NotesButton({ onClick }: { onClick: () => void }) {
  return (
    <NotesButtonStyled
      type="button"
      variant="primary"
      onClick={onClick}
      aria-label="Abrir bloco de anotações"
      data-testid="notesdrawer-button"
    >
      <FormOutlined />
      Anotações
      <NewTag>Novo</NewTag>
    </NotesButtonStyled>
  );
}

interface NotesDrawerProps {
  lessonId: number | string;
  open: boolean;
  onClose: () => void;
}

type NotesMode = 'text' | 'draw';

const TEXT_TAB_ID = 'notesdrawer-tab-text';
const DRAW_TAB_ID = 'notesdrawer-tab-draw';
const TEXT_PANEL_ID = 'notesdrawer-panel-text';
const DRAW_PANEL_ID = 'notesdrawer-panel-draw';

export function NotesDrawer({ lessonId, open, onClose }: NotesDrawerProps) {
  const [notes, setNotes] = useState(() => safeGetNotes(lessonId));
  // Sem suporte a desenho (sem Pointer Events/canvas), o painel segue só com o modo texto
  const drawingSupported = useMemo(() => isDrawingSupported(), []);
  const [mode, setMode] = useState<NotesMode>('text');
  const [tool, setTool] = useState<DrawTool>('pen');
  const [strokes, setStrokes] = useState<DrawingStroke[]>(() =>
    drawingSupported ? safeGetDrawing(lessonId) : [],
  );
  // Sinaliza quando o desenho não pôde ser gravado (cota cheia/storage indisponível)
  const [drawingPersistFailed, setDrawingPersistFailed] = useState(false);
  // Tamanho do painel escolhido pelo aluno (persistido em localStorage, vale para os dois modos)
  const { panelRef, panelStyle, resizeHandleProps } = useResizablePanel();

  useEffect(() => {
    safeSetNotes(lessonId, notes);
  }, [notes, lessonId]);

  useEffect(() => {
    if (drawingSupported) setDrawingPersistFailed(!safeSetDrawing(lessonId, strokes));
  }, [strokes, lessonId, drawingSupported]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Navegação por setas entre as abas, movendo seleção e foco (padrão de tablist)
  const handleModeToggleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const nextMode: NotesMode = mode === 'text' ? 'draw' : 'text';
    setMode(nextMode);
    document.getElementById(nextMode === 'text' ? TEXT_TAB_ID : DRAW_TAB_ID)?.focus();
  };

  return (
    <DrawerPanel
      ref={panelRef as React.Ref<HTMLElement>}
      style={panelStyle}
      open={open}
      aria-hidden={!open}
      data-testid="notesdrawer-panel"
    >
      <ResizeHandle {...resizeHandleProps} data-testid="notesdrawer-resize-handle" />
      <DrawerHeader>
        <DrawerTitle>Anotações</DrawerTitle>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label="Fechar bloco de anotações"
          data-testid="notesdrawer-close"
        >
          <img src={times} alt="Fechar" />
        </CloseButton>
      </DrawerHeader>

      <DrawerContent>
        <DrawerHint>
          Use este espaço para cálculos e observações
          {drawingSupported ? ', digitando ou desenhando' : ''}. Suas anotações ficam salvas
          durante o simulado e são descartadas ao finalizar a prova.
        </DrawerHint>

        {drawingPersistFailed && strokes.length > 0 && (
          <DrawerWarning role="alert" data-testid="notesdrawer-persist-warning">
            Não foi possível salvar o desenho — ele fica disponível apenas enquanto esta tela
            estiver aberta.
          </DrawerWarning>
        )}

        {drawingSupported && (
          <ModeToggle
            role="tablist"
            aria-label="Modo de anotação"
            onKeyDown={handleModeToggleKeyDown}
          >
            <ModeButton
              type="button"
              role="tab"
              id={TEXT_TAB_ID}
              aria-selected={mode === 'text'}
              aria-controls={TEXT_PANEL_ID}
              $active={mode === 'text'}
              onClick={() => setMode('text')}
              data-testid="notesdrawer-mode-text"
            >
              <FontSizeOutlined />
              Digitar
            </ModeButton>
            <ModeButton
              type="button"
              role="tab"
              id={DRAW_TAB_ID}
              aria-selected={mode === 'draw'}
              aria-controls={DRAW_PANEL_ID}
              $active={mode === 'draw'}
              onClick={() => setMode('draw')}
              data-testid="notesdrawer-mode-draw"
            >
              <HighlightOutlined />
              Desenhar
            </ModeButton>
          </ModeToggle>
        )}

        {/* Texto e desenho ficam sempre montados: alternar de modo não perde o que foi produzido */}
        <NotesTextArea
          id={TEXT_PANEL_ID}
          $visible={mode === 'text'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Digite suas anotações aqui..."
          aria-label="Bloco de anotações do simulado"
          data-testid="notesdrawer-textarea"
        />

        {drawingSupported && (
          <>
            {mode === 'draw' && (
              <DrawToolbar>
                <ToolButton
                  type="button"
                  $active={tool === 'pen'}
                  onClick={() => setTool('pen')}
                  aria-label="Caneta"
                  aria-pressed={tool === 'pen'}
                  title="Caneta"
                  data-testid="notesdrawer-tool-pen"
                >
                  <EditOutlined />
                </ToolButton>
                <ToolButton
                  type="button"
                  $active={tool === 'eraser'}
                  onClick={() => setTool('eraser')}
                  aria-label="Borracha"
                  aria-pressed={tool === 'eraser'}
                  title="Borracha"
                  data-testid="notesdrawer-tool-eraser"
                >
                  <ClearOutlined />
                </ToolButton>
                <ToolbarSpacer />
                <ToolButton
                  type="button"
                  onClick={() => setStrokes((prev) => prev.slice(0, -1))}
                  disabled={strokes.length === 0}
                  aria-label="Desfazer"
                  title="Desfazer"
                  data-testid="notesdrawer-undo"
                >
                  <UndoOutlined />
                </ToolButton>
                <ToolButton
                  type="button"
                  onClick={() => setStrokes([])}
                  disabled={strokes.length === 0}
                  aria-label="Limpar tudo"
                  title="Limpar tudo"
                  data-testid="notesdrawer-clear"
                >
                  <DeleteOutlined />
                </ToolButton>
              </DrawToolbar>
            )}

            <CanvasArea
              id={DRAW_PANEL_ID}
              role="tabpanel"
              aria-labelledby={DRAW_TAB_ID}
              $visible={mode === 'draw'}
            >
              <DrawingCanvas
                strokes={strokes}
                tool={tool}
                visible={open && mode === 'draw'}
                onCommitStroke={(stroke) => setStrokes((prev) => [...prev, stroke])}
              />
            </CanvasArea>
          </>
        )}
      </DrawerContent>
    </DrawerPanel>
  );
}
